import { logger } from '@/shared/utils';
/**
 * In-memory storage for File objects
 * Keeps File objects separate from Redux state to maintain serializability
 */
class FileStore {
   constructor() {
      this.files = new Map();
   }

   /**
    * Add a file to the store
    * @param {string} fileId - Unique identifier for the file
    * @param {File} file - File object to store
    */
   addFile(fileId, file) {
      if (!fileId || !file) {
         logger.error('FileStore.addFile: fileId and file are required');
         return;
      }
      this.files.set(fileId, file);
   }

   /**
    * Get a file from the store
    * @param {string} fileId - Unique identifier for the file
    * @returns {File|undefined} The file object or undefined if not found
    */
   getFile(fileId) {
      return this.files.get(fileId);
   }

   /**
    * Get multiple files by their IDs
    * @param {string[]} fileIds - Array of file IDs
    * @returns {Array<{fileId: string, file: File}>} Array of file objects with their IDs
    */
   getFiles(fileIds) {
      if (!Array.isArray(fileIds)) return [];
      return fileIds
         .map(fileId => ({
            fileId,
            file: this.files.get(fileId),
         }))
         .filter(item => item.file !== undefined);
   }

   /**
    * Remove a file from the store
    * @param {string} fileId - Unique identifier for the file
    * @returns {boolean} True if file was removed, false if not found
    */
   removeFile(fileId) {
      return this.files.delete(fileId);
   }

   /**
    * Remove multiple files from the store
    * @param {string[]} fileIds - Array of file IDs to remove
    */
   removeFiles(fileIds) {
      if (!Array.isArray(fileIds)) return;
      fileIds.forEach(fileId => this.files.delete(fileId));
   }

   /**
    * Clear all files from the store
    */
   clear() {
      this.files.clear();
   }

   /**
    * Get the number of files in the store
    * @returns {number} Number of files
    */
   size() {
      return this.files.size;
   }

   /**
    * Check if a file exists in the store
    * @param {string} fileId - Unique identifier for the file
    * @returns {boolean} True if file exists
    */
   hasFile(fileId) {
      return this.files.has(fileId);
   }
}

// Export a singleton instance
export const fileStore = new FileStore();
